/**
 * UnifiedTaskModal — Stage-driven task detail window
 *
 * Layout: full-screen dialog, three columns:
 *   Left:   vertical stepper of stage snapshots
 *   Center: selected stage form (DynamicFieldRenderer + image strip) OR monitoring view
 *   Right:  Details / Activity tabs
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent,
  Box, Typography, Chip, Button, IconButton,
  LinearProgress, Tabs, Tab, Divider,
  CircularProgress, Alert, Tooltip, Paper,
  TextField, Snackbar, Link,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
  X, CheckCircle, Clock, SkipForward, RotateCcw, Camera,
  BarChart2, Activity, Info, ExternalLink, Trash2, AlertCircle,
  ChevronRight, Lock,
} from 'lucide-react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import BASE_URL from '../../config';
import { usePermissions } from '../../contexts/PermissionContext';
import { useNavigate } from 'react-router-dom';
import DynamicFieldRenderer from '../Forms/DynamicFieldRenderer';
import ActivityTimeline from './ActivityTimeline';
import useAssignableUsers, { filterVendorCrewByProvider, renderAssignableMenuItems } from '../../hooks/useAssignableUsers';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const STAGE_STATUS_ICON = {
  completed: <CheckCircle size={16} color="#4CAF50" />,
  in_progress: <ChevronRight size={16} color="#FF9800" />,
  pending: <Clock size={16} color="#9E9E9E" />,
  skipped: <SkipForward size={16} color="#BDBDBD" />,
};

const STAGE_STATUS_COLOR = {
  completed: 'success',
  in_progress: 'warning',
  pending: 'default',
  skipped: 'default',
};

const STATUS_COLORS = { active: 'info', completed: 'success', cancelled: 'error' };

// ─────────────────────────────────────────────────────────────────────────────
// OverallProgress
// ─────────────────────────────────────────────────────────────────────────────
const OverallProgress = ({ stages }) => {
  const total = stages.length;
  const done = stages.filter(s => s.status === 'completed' || s.status === 'skipped').length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
      <LinearProgress variant="determinate" value={pct} sx={{ flex: 1, height: 8, borderRadius: 4 }} color={pct === 100 ? 'success' : 'primary'} />
      <Typography variant="caption">{done}/{total}</Typography>
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// StageImageStrip
// ─────────────────────────────────────────────────────────────────────────────
const StageImageStrip = ({ stageId, images, onUpload, readOnly }) => {
  const [uploading, setUploading] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const handleFiles = async (files) => {
    if (!files.length || readOnly) return;
    setUploading(true);
    try {
      await onUpload(stageId, Array.from(files));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
        {(images || []).map(img => (
          <Box
            key={img.id}
            component="img"
            src={img.photo_thumb}
            alt={img.description || ''}
            sx={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 1, cursor: 'pointer', border: '1px solid', borderColor: 'divider' }}
            onClick={() => setLightboxSrc(img.photo_thumb)}
            title={img.description || ''}
          />
        ))}
        {!readOnly && (
          <Box
            component="label"
            sx={{
              width: 60, height: 60, border: '2px dashed', borderColor: 'divider', borderRadius: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            <input type="file" accept="image/*" multiple hidden onChange={e => handleFiles(e.target.files)} />
            {uploading ? <CircularProgress size={18} /> : <Camera size={18} color="#aaa" />}
          </Box>
        )}
      </Box>

      {/* Simple lightbox */}
      {lightboxSrc && (
        <Dialog open maxWidth="md" onClose={() => setLightboxSrc(null)}>
          <Box component="img" src={lightboxSrc} alt="" sx={{ maxWidth: '100%', maxHeight: '80vh' }} />
        </Dialog>
      )}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MonitoringStageView
// ─────────────────────────────────────────────────────────────────────────────
const MonitoringStageView = ({ siteId, assetTypeId, metrics, stageStatus, onMarkReviewed, canUpdate }) => {
  const navigate = useNavigate();
  const isLake = assetTypeId === 2;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <BarChart2 size={20} color="#5B8DEF" />
        <Typography variant="h6">Monitoring & Impact Assessment</Typography>
      </Box>

      {metrics ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1.5, mb: 2 }}>
          {[
            { label: 'Carbon (tC)', value: metrics.carbon_tonne },
            { label: 'Natural Capital', value: metrics.natural_capital },
            { label: 'Greenness', value: metrics.greenness },
            { label: 'Temp Anomaly', value: metrics.temp_anomaly },
            { label: 'Tree Count', value: metrics.tree_count },
          ].map(kpi => (
            <Paper key={kpi.label} variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>{kpi.label}</Typography>
              <Typography variant="h6">{kpi.value != null ? Number(kpi.value).toLocaleString() : '—'}</Typography>
            </Paper>
          ))}
        </Box>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>No monitoring data yet for this site.</Alert>
      )}

      <Button
        variant="outlined"
        startIcon={<ExternalLink size={14} />}
        onClick={() => navigate(`/${isLake ? 'Lake' : 'ecological'}?site=${siteId}`)}
        sx={{ mb: 2 }}
      >
        Open Monitoring Dashboard
      </Button>

      {canUpdate && stageStatus !== 'completed' && (
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" color="success" onClick={onMarkReviewed} startIcon={<CheckCircle size={14} />}>
            Mark Reviewed & Complete
          </Button>
        </Box>
      )}
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main UnifiedTaskModal
// ─────────────────────────────────────────────────────────────────────────────
const UnifiedTaskModal = ({ open, taskId, onClose, onUpdated }) => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canUpdate = can('Task Management', 'taskAssignment', 'update');
  const canDelete = can('Task Management', 'taskAssignment', 'delete');

  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStageId, setSelectedStageId] = useState(null);
  const [rightTab, setRightTab] = useState(0);
  const [fieldValues, setFieldValues] = useState({});
  const [savingFields, setSavingFields] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [cancelReason, setCancelReason] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [stageContracts, setStageContracts] = useState([]);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [savingAssignee, setSavingAssignee] = useState(false);

  const showSnack = (msg, sev = 'success') => setSnack({ open: true, message: msg, severity: sev });

  const getToken = async () => {
    const user = getAuth().currentUser;
    return user ? await user.getIdToken() : null;
  };

  const fetchTask = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    setError('');
    try {
      const token = await getToken();
      const res = await axios.get(`${BASE_URL}/api/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data;
      setTask(data);

      // Auto-select in-progress or first stage
      if (!selectedStageId) {
        const inProgress = (data.stages || []).find(s => s.status === 'in_progress');
        setSelectedStageId(inProgress?.id || data.stages?.[0]?.id || null);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load task');
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    if (open && taskId) fetchTask();
  }, [open, taskId, fetchTask]);

  useEffect(() => {
    if (!task?.organisation_id) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${BASE_URL}/api/contractdropdown`, {
          params: { organisation_id: task.organisation_id },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setStageContracts(res.data || []);
      } catch (err) {
        console.error(err);
      }
    })();
    return () => { cancelled = true; };
  }, [task?.organisation_id]);

  const handleAssignmentChange = async (contractId) => {
    if (!selectedStage) return;
    setSavingAssignment(true);
    try {
      const token = await getToken();
      await axios.patch(
        `${BASE_URL}/api/tasks/${taskId}/stages/${selectedStage.id}/assignment`,
        { contract_id: contractId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnack('Assignment updated');
      await fetchTask();
    } catch (err) {
      showSnack(err.response?.data?.error || 'Failed to update assignment', 'error');
    } finally {
      setSavingAssignment(false);
    }
  };

  // Grouped assignee options (internal staff + vendor crew).
  const { staff, vendorCrew } = useAssignableUsers(task?.organisation_id);

  const handleAssigneeChange = async (userId) => {
    setSavingAssignee(true);
    try {
      const token = await getToken();
      await axios.put(
        `${BASE_URL}/api/tasks/${taskId}?organisation_id=${task.organisation_id}`,
        { assigned_to: userId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnack('Assignee updated');
      await fetchTask();
      onUpdated?.();
    } catch (err) {
      showSnack(err.response?.data?.error || 'Failed to update assignee', 'error');
    } finally {
      setSavingAssignee(false);
    }
  };

  // Populate field values when stage changes
  const selectedStage = task?.stages?.find(s => s.id === selectedStageId) || null;
  const filteredVendorCrew = filterVendorCrewByProvider(vendorCrew, selectedStage?.service_provider_id);
  useEffect(() => {
    if (!selectedStage) return;
    const vals = {};
    (selectedStage.fields || []).forEach(f => { vals[f.field_key] = f.value; });
    setFieldValues(vals);
  }, [selectedStageId, task]);

  const handleSaveFields = async () => {
    if (!selectedStage) return;
    setSavingFields(true);
    try {
      const token = await getToken();
      await axios.put(
        `${BASE_URL}/api/tasks/${taskId}/stages/${selectedStageId}/fields`,
        fieldValues,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnack('Fields saved');
      await fetchTask();
    } catch (err) {
      showSnack(err.response?.data?.error || 'Failed to save fields', 'error');
    } finally {
      setSavingFields(false);
    }
  };

  const handleStageTransition = async (status, extra = {}) => {
    setTransitioning(true);
    try {
      const token = await getToken();
      await axios.put(
        `${BASE_URL}/api/tasks/${taskId}/stages/${selectedStageId}`,
        { status, ...extra },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSnack(`Stage ${status === 'completed' ? 'completed' : status}`);
      await fetchTask();
      onUpdated?.();
    } catch (err) {
      showSnack(err.response?.data?.error || `Failed to ${status} stage`, 'error');
    } finally {
      setTransitioning(false);
    }
  };

  const handleUploadImages = async (stageId, files) => {
    const token = await getToken();
    const formData = new FormData();
    files.forEach(f => formData.append('photos', f));
    await axios.post(
      `${BASE_URL}/api/tasks/${taskId}/stages/${stageId}/images`,
      formData,
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
    );
    await fetchTask();
    showSnack('Photo uploaded');
  };

  const handleCancelTask = async () => {
    if (!cancelReason.trim()) return;
    try {
      const token = await getToken();
      await axios.put(`${BASE_URL}/api/tasks/${taskId}`, { status: 'cancelled', cancel_reason: cancelReason }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showSnack('Task cancelled');
      setCancelDialogOpen(false);
      await fetchTask();
      onUpdated?.();
    } catch (err) {
      showSnack('Failed to cancel task', 'error');
    }
  };

  const handleDeleteTask = async () => {
    if (!window.confirm('Delete this task? This cannot be undone.')) return;
    try {
      const token = await getToken();
      await axios.delete(`${BASE_URL}/api/tasks/${taskId}`, { headers: { Authorization: `Bearer ${token}` } });
      onUpdated?.();
      onClose();
    } catch (err) {
      showSnack('Failed to delete task', 'error');
    }
  };

  if (!open) return null;

  const stages = task?.stages || [];
  const totalStages = stages.length;
  const completedStages = stages.filter(s => s.status === 'completed').length;

  const firstIncompleteIndex = stages.findIndex(s => s.status !== 'completed' && s.status !== 'skipped');
  const isStageLocked = (idx) =>
    !!task?.enforce_sequential && firstIncompleteIndex !== -1 && idx > firstIncompleteIndex;
  const selectedStageIndex = stages.findIndex(s => s.id === selectedStageId);
  const selectedStageLocked = isStageLocked(selectedStageIndex);

  const stageElapsedLabel = (stage) => {
    if (stage.stage_type === 'monitoring') return null;
    if (stage.status === 'completed' && stage.started_at && stage.completed_at) {
      const days = Math.round((new Date(stage.completed_at) - new Date(stage.started_at)) / 86400000);
      return { text: `took ${days}d`, stalled: false };
    }
    if (stage.status === 'in_progress' && stage.started_at) {
      const elapsed = Math.floor((Date.now() - new Date(stage.started_at)) / 86400000);
      const expected = stage.expected_days ?? 30;
      const stalled = elapsed > expected;
      return { text: `${stalled ? '⏳ ' : ''}${elapsed}d${stalled ? ` — expected ${expected}d` : ''}`, stalled };
    }
    return null;
  };

  const requiredFieldsMissing = selectedStage && selectedStage.stage_type === 'standard'
    ? (selectedStage.fields || []).filter(f => f.is_required && (fieldValues[f.field_key] === null || fieldValues[f.field_key] === undefined || fieldValues[f.field_key] === ''))
    : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { height: '92vh', display: 'flex', flexDirection: 'column' } }}
    >
      {/* Header */}
      <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 1.5 }}>
        {loading ? (
          <Typography>Loading…</Typography>
        ) : task ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
                {task.title}
              </Typography>
              {task.maintenance_plan_id && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Part of plan: {task.maintenance_plan_name || '—'}
                    {task.due_date && ` · due ${new Date(task.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}`}
                  </Typography>
                  {task.outcome && (
                    <Chip
                      size="small"
                      label={task.outcome === 'on_time' ? 'On time' : task.outcome.charAt(0).toUpperCase() + task.outcome.slice(1)}
                      color={task.outcome === 'on_time' ? 'success' : task.outcome === 'delayed' ? 'warning' : 'error'}
                      sx={{ height: 18, fontSize: 10 }}
                    />
                  )}
                </Box>
              )}
            </Box>
            <Chip label={task.status} size="small" color={STATUS_COLORS[task.status] || 'default'} />
            {totalStages > 0 && <OverallProgress stages={stages} />}
            {canUpdate && task.status === 'active' && (
              <Tooltip title="Cancel task">
                <IconButton size="small" color="warning" onClick={() => setCancelDialogOpen(true)}>
                  <AlertCircle size={16} />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete task">
                <IconButton size="small" color="error" onClick={handleDeleteTask}>
                  <Trash2 size={16} />
                </IconButton>
              </Tooltip>
            )}
            <IconButton size="small" onClick={onClose}><X size={18} /></IconButton>
          </Box>
        ) : null}
      </DialogTitle>

      {/* Body */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ flex: 1, p: 3 }}><Alert severity="error">{error}</Alert></Box>
        ) : task ? (
          <>
            {/* ── Left: Stage stepper ── */}
            <Box sx={{ width: 220, borderRight: '1px solid', borderColor: 'divider', overflowY: 'auto', py: 1 }}>
              {stages.map((stage, idx) => {
                const locked = isStageLocked(idx);
                const elapsed = stageElapsedLabel(stage);
                return (
                  <Box
                    key={stage.id}
                    onClick={() => setSelectedStageId(stage.id)}
                    sx={{
                      px: 2, py: 1.5, cursor: 'pointer',
                      bgcolor: stage.id === selectedStageId ? 'action.selected' : 'transparent',
                      borderLeft: stage.id === selectedStageId ? '3px solid' : '3px solid transparent',
                      borderLeftColor: stage.id === selectedStageId ? 'primary.main' : 'transparent',
                      opacity: locked ? 0.55 : 1,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {locked
                        ? <Lock size={14} color="#9E9E9E" />
                        : stage.stage_type === 'monitoring'
                          ? <BarChart2 size={14} color="#5B8DEF" />
                          : STAGE_STATUS_ICON[stage.status]}
                      <Typography
                        variant="body2"
                        fontWeight={stage.id === selectedStageId ? 600 : 400}
                        color={stage.status === 'skipped' || locked ? 'text.disabled' : 'text.primary'}
                        noWrap
                      >
                        {stage.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                      <Chip
                        label={locked ? 'locked' : stage.status.replace('_', ' ')}
                        size="small"
                        color={locked ? 'default' : STAGE_STATUS_COLOR[stage.status]}
                        sx={{ fontSize: 10 }}
                      />
                      {elapsed && (
                        <Typography variant="caption" color={elapsed.stalled ? 'error.main' : 'text.secondary'}>
                          {elapsed.text}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* ── Center: Stage detail ── */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
              {!selectedStage ? (
                <Typography color="text.secondary">Select a stage to view details.</Typography>
              ) : selectedStage.stage_type === 'monitoring' ? (
                <MonitoringStageView
                  siteId={task.site_id}
                  assetTypeId={task.asset_type_id}
                  metrics={task.latest_metrics}
                  stageStatus={selectedStage.status}
                  canUpdate={canUpdate && task.status === 'active' && !selectedStageLocked}
                  onMarkReviewed={() => handleStageTransition('completed')}
                />
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6">{selectedStage.name}</Typography>
                      {selectedStage.description && (
                        <Typography variant="body2" color="text.secondary">{selectedStage.description}</Typography>
                      )}
                    </Box>
                    <Chip label={selectedStageLocked ? 'locked' : selectedStage.status.replace('_', ' ')} color={selectedStageLocked ? 'default' : STAGE_STATUS_COLOR[selectedStage.status]} size="small" />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    {canUpdate && task.status === 'active' && !selectedStageLocked ? (
                      <FormControl size="small" sx={{ minWidth: 240 }} disabled={savingAssignment}>
                        <InputLabel>Service provider (via contract)</InputLabel>
                        <Select
                          value={selectedStage.contract_id || ''}
                          label="Service provider (via contract)"
                          onChange={e => handleAssignmentChange(e.target.value || null)}
                        >
                          <MenuItem value="">None</MenuItem>
                          {stageContracts.map(c => (
                            <MenuItem key={c.id} value={c.id}>{c.service_provider_name} ({c.name})</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : selectedStage.contract_id ? (
                      <Typography variant="body2" color="text.secondary">
                        Service provider: {selectedStage.service_provider_name || '—'} (Contract {selectedStage.contract_name || '—'})
                      </Typography>
                    ) : null}
                  </Box>

                  {selectedStageLocked && (
                    <Alert severity="info" icon={<Lock size={16} />} sx={{ mb: 2 }}>
                      Complete previous stages to unlock.
                    </Alert>
                  )}

                  <Divider sx={{ mb: 2 }} />

                  {/* Fields */}
                  <DynamicFieldRenderer
                    fields={selectedStage.fields || []}
                    values={fieldValues}
                    onChange={(key, val) => setFieldValues(prev => ({ ...prev, [key]: val }))}
                    readOnly={!canUpdate || task.status !== 'active' || selectedStage.status === 'completed' || selectedStageLocked}
                  />

                  {/* Images */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Photos</Typography>
                    <StageImageStrip
                      stageId={selectedStage.id}
                      images={selectedStage.images || []}
                      onUpload={handleUploadImages}
                      readOnly={!canUpdate || task.status !== 'active' || selectedStageLocked}
                    />
                  </Box>

                  {/* Actions */}
                  {canUpdate && task.status === 'active' && !selectedStageLocked && (
                    <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {(selectedStage.fields || []).length > 0 && selectedStage.status !== 'completed' && (
                        <Button
                          variant="outlined"
                          startIcon={savingFields ? <CircularProgress size={14} /> : null}
                          onClick={handleSaveFields}
                          disabled={savingFields || transitioning}
                        >
                          Save fields
                        </Button>
                      )}

                      {selectedStage.status === 'completed' ? (
                        <Button
                          startIcon={<RotateCcw size={14} />}
                          onClick={() => handleStageTransition('in_progress')}
                          disabled={transitioning}
                          color="warning"
                        >
                          Reopen stage
                        </Button>
                      ) : (
                        <>
                          <Tooltip title={requiredFieldsMissing.length ? `Fill required fields: ${requiredFieldsMissing.map(f => f.label).join(', ')}` : ''}>
                            <span>
                              <Button
                                variant="contained"
                                color="success"
                                startIcon={transitioning ? <CircularProgress size={14} /> : <CheckCircle size={14} />}
                                onClick={() => handleSaveFields().then(() => handleStageTransition('completed'))}
                                disabled={transitioning || savingFields || requiredFieldsMissing.length > 0}
                              >
                                Mark complete
                              </Button>
                            </span>
                          </Tooltip>

                          <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<SkipForward size={14} />}
                            onClick={() => setSkipDialogOpen(true)}
                            disabled={transitioning}
                          >
                            Skip stage
                          </Button>
                        </>
                      )}
                      {task.maintenance_plan_id && (
                        <Button
                          variant="text"
                          startIcon={<ExternalLink size={14} />}
                          onClick={() => navigate(`/${task.asset_type_id === 2 ? 'Lake' : 'ecological'}?site=${task.site_id}&grid=${task.grid_id || ''}`)}
                        >
                          Request inspection
                        </Button>
                      )}
                    </Box>
                  )}

                  {requiredFieldsMissing.length > 0 && canUpdate && task.status === 'active' && selectedStage.status !== 'completed' && (
                    <Alert severity="warning" icon={<AlertCircle size={16} />} sx={{ mt: 1 }}>
                      Required fields not filled: {requiredFieldsMissing.map(f => f.label).join(', ')}
                    </Alert>
                  )}
                </Box>
              )}
            </Box>

            {/* ── Right: Details / Activity ── */}
            <Box sx={{ width: 260, borderLeft: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
              <Tabs value={rightTab} onChange={(_, v) => setRightTab(v)} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Tab label="Details" icon={<Info size={14} />} iconPosition="start" sx={{ minHeight: 40, fontSize: 12 }} />
                <Tab label="Activity" icon={<Activity size={14} />} iconPosition="start" sx={{ minHeight: 40, fontSize: 12 }} />
              </Tabs>

              <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                {rightTab === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Assignee</Typography>
                      {canUpdate && task.status === 'active' ? (
                        <FormControl size="small" fullWidth disabled={savingAssignee}>
                          <Select value={task.assigned_to || ''} displayEmpty onChange={e => handleAssigneeChange(e.target.value)}>
                            <MenuItem value=""><em>Unassigned</em></MenuItem>
                            {renderAssignableMenuItems(staff, filteredVendorCrew)}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body2">{task.assigned_to_name || '—'}</Typography>
                      )}
                    </Box>
                    {[
                      { label: 'Priority', value: task.priority ? task.priority.charAt(0).toUpperCase() + task.priority.slice(1) : '—' },
                      { label: 'Due date', value: task.due_date ? new Date(task.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—' },
                      { label: 'Site', value: task.site_name || task.site_id },
                      { label: 'Plot', value: task.sub_site_name || '—' },
                      { label: 'Workflow', value: task.workflow_name || '—' },
                      { label: 'Contract', value: task.contract_name || '—' },
                      { label: 'Created by', value: task.created_by_name || '—' },
                    ].map(({ label, value }) => (
                      <Box key={label}>
                        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                        <Typography variant="body2">{value}</Typography>
                      </Box>
                    ))}
                    {task.cancel_reason && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        <Typography variant="caption">Cancel reason: {task.cancel_reason}</Typography>
                      </Alert>
                    )}
                  </Box>
                ) : (
                  <ActivityTimeline activities={task.activity || []} />
                )}
              </Box>
            </Box>
          </>
        ) : null}
      </Box>

      {/* Cancel task dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel task</DialogTitle>
        <Box sx={{ p: 2 }}>
          <TextField
            label="Reason for cancellation"
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
            fullWidth
            multiline
            rows={2}
            size="small"
            autoFocus
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', p: 2, pt: 0 }}>
          <Button onClick={() => setCancelDialogOpen(false)}>Back</Button>
          <Button variant="contained" color="error" onClick={handleCancelTask} disabled={!cancelReason.trim()}>
            Cancel task
          </Button>
        </Box>
      </Dialog>

      {/* Skip stage dialog */}
      <Dialog open={skipDialogOpen} onClose={() => setSkipDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Skip stage: {selectedStage?.name}</DialogTitle>
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            This stage will be marked as skipped. Progress will advance to the next pending stage.
            You can reopen any stage later if needed.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', p: 2, pt: 0 }}>
          <Button onClick={() => setSkipDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={() => { setSkipDialogOpen(false); handleStageTransition('skipped'); }}>
            Skip stage
          </Button>
        </Box>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default UnifiedTaskModal;
